<template>
  <div
    class="container mx-auto mt-12 md:mt-18 lg:mt-20 xl:mt-24 md:mb-18 mb-12"
  >
    <div class="px-4">
      <h1
        class="text-center mx-auto max-w-6xl text-3xl lg:text-5xl mb-10"
        v-html="dataLocales.data.title"
      ></h1>
      <p class="text-lg md:text-xl text-center">
        {{ dataLocales.data.description }}
      </p>
      <div class="mt-24 lg:gap-x-12 gap-y-12 grid grid-cols-12">
        <div
          class="col-span-12 lg:col-span-6 h-full flex items-center justify-center lg:flex-col xs:flex-row flex-col gap-12"
        >
          <div
            class="px-8 py-9 rounded-md drop-shadow-md bg-white w-full mx-auto max-w-md"
          >
            <div
              class="mb-10 w-20 h-20 mx-auto drop-shadow-md rounded-full bg-gradient-to-r from-pink-500 via-pink-600 to-success flex items-center justify-center -mt-16"
            >
              <MailIcon size="32" class="text-white" />
            </div>
            <div class="text-center">
              <h3 class="text-xl mb-6">Email</h3>
              <a
                href="mailto:info@qrdove.com"
                class="text-lg hover:text-success"
                >info@qrdove.com</a
              >
            </div>
          </div>
          <div
            class="px-8 py-9 rounded-md drop-shadow-md bg-white w-full mx-auto max-w-md"
          >
            <div
              class="mb-10 w-20 h-20 mx-auto drop-shadow-md rounded-full bg-gradient-to-r from-pink-500 via-pink-600 to-success flex items-center justify-center -mt-16"
            >
              <DeviceLandlinePhoneIcon size="32" class="text-white" />
            </div>
            <div class="text-center">
              <h3 class="text-xl mb-6">{{ dataLocales.data.number_phone }}</h3>
              <a href="tel:+44 2039106889" class="text-lg hover:text-success"
                >+44 2039106889                </a
              >
            </div>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-6 sm:px-10 px-4">
          <h2
            class="text-2xl sm:text-3xl"
            v-html="dataLocales.data.title2"
          ></h2>
          <form @submit.prevent="submitContact()" class="mt-12 text-base">
            <div class="mb-5 relative">
              <label for="" class="absolute pl-4 leading-[60px]">{{
                dataLocales.data.fullname
              }}</label>
              <input
                required
                type="text"
                class="h-[60px] w-full rounded-md bg-gray-100 focus:outline-2 focus:outline-success focus:bg-white pl-2 focus:pl-28 transition-all ease-in-out"
                :class="{ 'pl-28': form.name }"
                v-model="form.name"
              />
            </div>
            <div class="mb-5 relative">
              <label for="" class="absolute pl-4 leading-[60px]">Email</label>
              <input
                required
                type="email"
                class="h-[60px] w-full rounded-md bg-gray-100 focus:outline-2 focus:outline-success focus:bg-white pl-2 focus:pl-28 transition-all ease-in-out"
                :class="{ 'pl-28': form.email }"
                v-model="form.email"
              />
            </div>
            <div class="mb-5 relative">
              <label for="" class="absolute pl-4 leading-[60px]"
                >{{ dataLocales.data.phone }}
              </label>
              <input
                type="text"
                class="h-[60px] w-full rounded-md bg-gray-100 focus:outline-2 focus:outline-success focus:bg-white pl-2 focus:pl-28 transition-all ease-in-out"
                :class="{ 'pl-28': form.phone }"
                v-model="form.phone"
              />
            </div>
            <div class="mb-5 relative">
              <label for="" class="absolute pl-4 leading-[60px]">{{
                dataLocales.data.subject
              }}</label>
              <input
                type="text"
                class="h-[60px] w-full rounded-md bg-gray-100 focus:outline-2 focus:outline-success focus:bg-white pl-2 focus:pl-28 transition-all ease-in-out"
                :class="{ 'pl-28': form.subject }"
                v-model="form.subject"
              />
            </div>
            <div class="mb-5 relative">
              <textarea
                required
                v-model="form.message"
                rows="5"
                :placeholder="dataLocales.data.message"
                class="p-4 bg-gray-100 accent-black rounded-md focus:outline-2 focus:outline-success focus:bg-white w-full"
              ></textarea>
            </div>
            <div
              v-if="submitted"
              class="sm:flex-row flex-col flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 rounded-2xl py-6 sm:py-4 sm:px-6 p-3 my-8 bg-success-soft"
            >
              <div class="flex-grow flex gap-1">
                <CircleCheckFilledIcon class="text-success" />
                <p class="sm:text-left sm:line-clamp-1">
                  {{ dataLocales.data.thank }}
                </p>
              </div>
              <a
                target="_blank"
                :href="runtimeConfig.public.app_user_url + '/sign-up'"
                class="flex-shrink-0 w-fit px-6 py-2 text text-md shadow bg-white rounded-lg hover:shadow-md"
              >
                {{ $t("start_for_free") }}
              </a>
            </div>
            <button
              type="submit"
              :disabled="loading"
              class="flex items-center gap-2 h-10 xs:h-[50px] px-8 rounded-md bg-success text-white drop-shadow-md hover:drop-shadow-xl mt-12"
            >
              <IconsLoadingDecorator class="w-4" v-if="loading" />
              {{ dataLocales.data.send }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { CircleCheckFilledIcon } from "vue-tabler-icons";
import { useNotification } from "@kyvg/vue3-notification";
import { MailIcon, DeviceLandlinePhoneIcon } from "vue-tabler-icons";
import DataLocale from "~~/config/interface/DataLocale";
import moment from "moment";
const { notify } = useNotification();
const runtimeConfig = useRuntimeConfig();
const submitted = ref(false);

const dataLocales = new DataLocale([
  {
    locale: "en",
    title: "Get in Touch <span class='text-success'>With Us</span>",
    description:
      "Everything you need to know about QR Code creation, design, and scanning.",
    title2:
      "Have Questions? <br> <span class='down-mark-line text-success'>Let’s Begin</span> the Conversation",
    fullname: "Your Full Name",
    number_phone: "Your Phone Number",
    phone: "Contact Number",
    subject: "Subject of Inquiry",
    message: "Your Message",
    send: "Send Your Message",
    thank: "Thank you for reaching out. We’ll get back to you shortly!",
  },
  {
    locale: "fr",
    title: "Contactez-nous <span class='text-success'></span>",
    description:
      "Tout ce que vous devez savoir sur la création, la conception et la numérisation de codes QR.",
    title2:
      "Vous avez des questions ? <br> <span class='down-mark-line text-success'>Commençons</span> la conversation",
    fullname: "Votre nom complet",
    number_phone: "Votre numéro de téléphone",
    phone: "Numéro de contact",
    subject: "Objet de la demande",
    message: "Votre message",
    send: "Envoyer votre message",
    thank:
      "Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais !",
  },
  {
    locale: "it",
    title: "Mettiti in contatto <span class='text-success'>con noi</span>",
    description:
      "Tutto ciò che devi sapere sulla creazione, la progettazione e la scansione dei codici QR.",
    title2:
      "Hai domande? <br> <span class='down-mark-line text-success'>Iniziamo</span> la conversazione",
    fullname: "Il tuo nome completo",
    number_phone: "Il tuo numero di telefono",
    phone: "Numero di contatto",
    subject: "Oggetto della richiesta",
    message: "Il tuo messaggio",
    send: "Invia il tuo messaggio",
    thank: "Grazie per averci contattato. Ti risponderemo al più presto!",
  },
  {
    locale: "de",
    title: "Kontaktieren Sie uns <span class='text-success'></span>",
    description:
      "Alles, was Sie über die Erstellung, das Design und das Scannen von QR-Codes wissen müssen.",
    title2:
      "Haben Sie Fragen? <br> <span class='down-mark-line text-success'>Lassen Sie uns</span> das Gespräch beginnen",
    fullname: "Ihr vollständiger Name",
    number_phone: "Ihre Telefonnummer",
    phone: "Kontaktnummer",
    subject: "Betreff der Anfrage",
    message: "Ihre Nachricht",
    send: "Senden Sie Ihre Nachricht",
    thank:
      "Vielen Dank, dass Sie sich gemeldet haben. Wir werden uns in Kürze bei Ihnen melden!",
  },
  {
    locale: "es",
    title: "Póngase en contacto <span class='text-success'>con nosotros</span>",
    description:
      "Todo lo que necesita saber sobre la creación, el diseño y el escaneo de códigos QR.",
    title2:
      "¿Tiene preguntas? <br> <span class='down-mark-line text-success'>Comencemos</span> la conversación",
    fullname: "Su nombre completo",
    number_phone: "Su número de teléfono",
    phone: "Número de contacto",
    subject: "Asunto de la consulta",
    message: "Su mensaje",
    send: "Enviar su mensaje",
    thank:
      "Gracias por contactarnos. ¡Nos pondremos en contacto con usted en breve!",
  },
  {
    locale: "nl",
    title: "Neem contact <span class='text-success'>met ons</span> op",
    description:
      "Alles wat u moet weten over het maken, ontwerpen en scannen van QR-codes.",
    title2:
      "Heeft u vragen? <br> <span class='down-mark-line text-success'>Laten we</span> het gesprek beginnen",
    fullname: "Uw volledige naam",
    number_phone: "Uw telefoonnummer",
    phone: "Contactnummer",
    subject: "Onderwerp van de vraag",
    message: "Uw bericht",
    send: "Verstuur uw bericht",
    thank:
      "Bedankt dat u contact met ons heeft opgenomen. We nemen zo snel mogelijk contact met u op!",
  },
  {
    locale: "pt",
    title: "Entre em contato <span class='text-success'>Conosco</span>",
    description:
      "Tudo o que você precisa saber sobre a criação, design e digitalização de código QR.",
    title2:
      "Tem perguntas? <br> <span class='down-mark-line text-success'>Vamos começar</span> a conversa",
    fullname: "Seu nome completo",
    number_phone: "Seu número de telefone",
    phone: "Número de contato",
    subject: "Assunto da consulta",
    message: "Sua mensagem",
    send: "Envie sua mensagem",
    thank: "Obrigado por entrar em contato. Entraremos em contato em breve!",
  },
  {
    locale: "ja",
    title: "お問い合わせ<span class='text-success'>ください</span>",
    description:
      "QRコードの作成、デザイン、スキャンについて知っておくべきことのすべて。",
    title2:
      "ご質問はありますか？ <br> <span class='down-mark-line text-success'>会話を始めましょう</span>",
    fullname: "あなたのフルネーム",
    number_phone: "あなたの電話番号",
    phone: "連絡先電話番号",
    subject: "お問い合わせの件名",
    message: "あなたのメッセージ",
    send: "メッセージを送信",
    thank: "お問い合わせありがとうございます。すぐにご連絡いたします！",
  },
  {
    locale: "zh",
    title: "与我们<span class='text-success'>联系</span>",
    description: "关于二维码创建、设计和扫描，您需要了解的一切。",
    title2:
      "有疑问吗？ <br> <span class='down-mark-line text-success'>让我们开始</span>对话",
    fullname: "您的全名",
    number_phone: "您的电话号码",
    phone: "联系电话",
    subject: "咨询主题",
    message: "您的留言",
    send: "发送您的留言",
    thank: "感谢您的联系。我们会尽快回复您！",
  },
  {
    locale: "vi",
    title: "Liên Hệ <span class='text-success'>Với Chúng Tôi</span>",
    description: "Mọi điều bạn cần biết về việc tạo, thiết kế và quét mã QR.",
    title2:
      "Bạn Có Câu Hỏi? <br> <span class='down-mark-line text-success'>Hãy Bắt Đầu</span> Cuộc Trò Chuyện",
    fullname: "Họ Và Tên Của Bạn",
    number_phone: "Số Điện Thoại Của Bạn",
    phone: "Số Điện Thoại Liên Hệ",
    subject: "Chủ Đề Thắc Mắc",
    message: "Tin Nhắn Của Bạn",
    send: "Gửi Tin Nhắn",
    thank:
      "Cảm ơn bạn đã liên hệ. Chúng tôi sẽ liên hệ lại với bạn trong thời gian ngắn!",
  },
  {
    locale: "id",
    title: "Hubungi <span class='text-success'>Kami</span>",
    description:
      "Semua yang perlu Anda ketahui tentang pembuatan, desain, dan pemindaian Kode QR.",
    title2:
      "Punya Pertanyaan? <br> <span class='down-mark-line text-success'>Mari Mulai</span> Percakapan",
    fullname: "Nama Lengkap Anda",
    number_phone: "Nomor Telepon Anda",
    phone: "Nomor Kontak",
    subject: "Subjek Pertanyaan",
    message: "Pesan Anda",
    send: "Kirim Pesan Anda",
    thank:
      "Terima kasih telah menghubungi kami. Kami akan segera menghubungi Anda!",
  },
  {
    locale: "tr",
    title: "<span class='text-success'>Bizimle</span> İletişime Geçin",
    description:
      "QR Kodu oluşturma, tasarım ve tarama hakkında bilmeniz gereken her şey.",
    title2:
      "Sorularınız mı Var? <br> <span class='down-mark-line text-success'>Sohbete</span> Başlayalım",
    fullname: "Adınız Soyadınız",
    number_phone: "Telefon Numaranız",
    phone: "İletişim Numarası",
    subject: "Soru Konusu",
    message: "Mesajınız",
    send: "Mesajınızı Gönderin",
    thank:
      "Bize ulaştığınız için teşekkür ederiz. En kısa sürede size geri döneceğiz!",
  },
  {
    locale: "hi",
    title: "<span class='text-success'>हमसे</span> संपर्क करें",
    description:
      "क्यूआर कोड निर्माण, डिज़ाइन और स्कैनिंग के बारे में आपको जो कुछ भी जानने की आवश्यकता है।",
    title2:
      "क्या आपके कोई प्रश्न हैं? <br> <span class='down-mark-line text-success'>आइए</span> बातचीत शुरू करें",
    fullname: "आपका पूरा नाम",
    number_phone: "आपका फ़ोन नंबर",
    phone: "संपर्क नंबर",
    subject: "पूछताछ का विषय",
    message: "आपका संदेश",
    send: "अपना संदेश भेजें",
    thank: "हमसे संपर्क करने के लिए धन्यवाद। हम जल्द ही आपसे संपर्क करेंगे!",
  },
  {
    locale: "th",
    title: "ติดต่อ<span class='text-success'>เรา</span>",
    description:
      "ทุกสิ่งที่คุณจำเป็นต้องรู้เกี่ยวกับการสร้าง การออกแบบ และการสแกนรหัส QR",
    title2:
      "มีคำถามไหม? <br> <span class='down-mark-line text-success'>มาเริ่ม</span>บทสนทนากัน",
    fullname: "ชื่อเต็มของคุณ",
    number_phone: "หมายเลขโทรศัพท์ของคุณ",
    phone: "หมายเลขติดต่อ",
    subject: "หัวข้อการสอบถาม",
    message: "ข้อความของคุณ",
    send: "ส่งข้อความของคุณ",
    thank: "ขอบคุณที่ติดต่อเรา เราจะติดต่อกลับหาคุณในไม่ช้า!",
  },
]);

const form = ref({
  email: "",
  phone: "",
  name: "",
  subject: "",
  message: "",
});
const loading = ref(false);
const submitContact = () => {
  loading.value = true;
  $fetch("/api/contact", {
    method: "POST",
    body: form.value,
  })
    .then((res) => {
      loading.value = false;
      notify({
        type: "success",
        text: "Thank you for getting in touch!",
      });
      submitted.value = true;
    })
    .catch((err) => {
      loading.value = false;
    });
};

const seoheader = new DataLocale([
  {
    locale: "en",
    title: "Contact Us - QR Dove",
    description:
      "Get in touch with us for support, feedback, or suggestions. Our team is ready to assist you with any inquiries or issues you may have. Simply use our contact form to send us a message, and we'll get back to you as soon as possible. We value your feedback and strive to provide excellent customer service.",
  },
  {
    locale: "fr",
    title: "Contactez-nous - QR Dove",
    description:
      "Contactez-nous pour obtenir du soutien, des commentaires ou des suggestions. Notre équipe est prête à vous aider pour toute demande d'information ou problème que vous pourriez rencontrer. Utilisez simplement notre formulaire de contact pour nous envoyer un message et nous vous répondrons dès que possible. Nous attachons de l'importance à vos commentaires et nous nous efforçons de fournir un excellent service client.",
  },
  {
    locale: "de",
    title: "Kontaktieren Sie uns - QR Dove",
    description:
      "Kontaktieren Sie uns für Unterstützung, Feedback oder Vorschläge. Unser Team steht Ihnen bei allen Anfragen oder Problemen zur Verfügung. Verwenden Sie einfach unser Kontaktformular, um uns eine Nachricht zu senden, und wir werden uns so schnell wie möglich bei Ihnen melden. Wir schätzen Ihr Feedback und streben exzellenten Kundenservice an.",
  },
  {
    locale: "es",
    title: "Contáctenos - QR Dove",
    description:
      "Póngase en contacto con nosotros para obtener soporte, comentarios o sugerencias. Nuestro equipo está listo para ayudarlo con cualquier consulta o problema que pueda tener. Simplemente utilice nuestro formulario de contacto para enviarnos un mensaje, y le responderemos lo antes posible. Valoramos sus comentarios y nos esforzamos por brindar un excelente servicio al cliente.",
  },
  {
    locale: "it",
    title: "Contattaci - QR Dove",
    description:
      "Contattaci per supporto, feedback o suggerimenti. Il nostro team è pronto ad assisterti per qualsiasi richiesta o problema che potresti avere. Usa il nostro modulo di contatto per inviarci un messaggio e ti risponderemo al più presto. Valorizziamo il tuo feedback e ci impegniamo a fornire un eccellente servizio clienti.",
  },
  {
    locale: "nl",
    title: "Contact met ons opnemen - QR Dove",
    description:
      "Neem contact met ons op voor ondersteuning, feedback of suggesties. Ons team staat klaar om u te helpen met vragen of problemen die u kunt hebben. Gebruik eenvoudig ons contactformulier om ons een bericht te sturen, en we zullen zo snel mogelijk terugkomen. We waarderen uw feedback en streven naar uitstekende klantenservice.",
  },
  {
    locale: "zh",
    title: "联系我们- QR Dove",
    description:
      "如需支持、反馈或建议，请与我们联系。我们的团队随时准备好协助您处理任何疑问或问题。只需使用我们的联系表格向我们发送消息，我们将尽快回复您。我们重视您的反馈，并努力提供卓越的客户服务。",
  },
  {
    locale: "ja",
    title: "お問い合わせ - QR Dove",
    description:
      "サポート、フィードバック、提案についてお問い合わせください。弊社のチームは、お客様がお持ちの問い合わせや問題についてお手伝いいたします。お問い合わせフォームからメッセージを送信するだけで、迅速に対応いたします。お客様のフィードバックを大切にし、優れたカスタマーサービスを提供することを目指しています。",
  },
  {
    locale: "vi",
    title: "Liên hệ - QR Dove",
    description:
      "Liên hệ với chúng tôi để được hỗ trợ, gửi góp ý hoặc phản hồi. Đội ngũ của chúng tôi sẵn sàng hỗ trợ bạn với bất kỳ thắc mắc hoặc vấn đề nào mà bạn có. Chỉ cần sử dụng biểu mẫu liên hệ để gửi cho chúng tôi một tin nhắn và chúng tôi sẽ phản hồi bạn sớm nhất có thể. Chúng tôi đánh giá cao ý kiến đóng góp của bạn và luôn cố gắng cung cấp dịch vụ khách hàng tốt nhất.",
  },
  {
    locale: "pt",
    title: "Contate-nos - QR Dove",
    description:
      "Entre em contato conosco para obter suporte, feedback ou sugestões. Nossa equipe está pronta para ajudá-lo com quaisquer dúvidas ou problemas que você possa ter. Basta usar nosso formulário de contato para nos enviar uma mensagem e entraremos em contato o mais breve possível. Valorizamos seus comentários e nos esforçamos para fornecer um excelente atendimento ao cliente.",
  },
  {
    locale: "tr",
    title: "Bize Ulaşın - QR Dove",
    description:
      "Destek, geri bildirim veya önerileriniz için bizimle iletişime geçin. Ekibimiz, sahip olabileceğiniz herhangi bir soru veya sorun konusunda size yardımcı olmaya hazır. Bize bir mesaj göndermek için iletişim formumuzu kullanmanız yeterli; en kısa sürede size geri dönüş yapacağız. Geri bildiriminize değer veriyoruz ve mükemmel müşteri hizmeti sunmaya çalışıyoruz.",
  },
  {
    locale: "hi",
    title: "हमसे संपर्क करें - QR Dove",
    description:
      "समर्थन, प्रतिक्रिया या सुझावों के लिए हमसे संपर्क करें। हमारी टीम आपके किसी भी पूछताछ या समस्या में आपकी सहायता करने के लिए तैयार है। हमें संदेश भेजने के लिए बस हमारे संपर्क फ़ॉर्म का उपयोग करें, और हम जल्द से जल्द आपसे संपर्क करेंगे। हम आपकी प्रतिक्रिया को महत्व देते हैं और उत्कृष्ट ग्राहक सेवा प्रदान करने का प्रयास करते हैं।",
  },
  {
    locale: "id",
    title: "Hubungi Kami - QR Dove",
    description:
      "Hubungi kami untuk dukungan, umpan balik, atau saran. Tim kami siap membantu Anda dengan pertanyaan atau masalah apa pun yang mungkin Anda miliki. Cukup gunakan formulir kontak kami untuk mengirim pesan kepada kami, dan kami akan menghubungi Anda sesegera mungkin. Kami menghargai umpan balik Anda dan berusaha untuk memberikan layanan pelanggan yang sangat baik.",
  },
  {
    locale: "th",
    title: "ติดต่อเรา - QR Dove",
    description:
      "ติดต่อเราเพื่อขอความช่วยเหลือ ข้อเสนอแนะ หรือคำแนะนำ ทีมงานของเราพร้อมที่จะช่วยเหลือคุณในทุกข้อสงสัยหรือปัญหาที่คุณอาจมี เพียงใช้แบบฟอร์มการติดต่อของเราเพื่อส่งข้อความถึงเรา แล้วเราจะติดต่อกลับหาคุณโดยเร็วที่สุด เราให้ความสำคัญกับความคิดเห็นของคุณและมุ่งมั่นที่จะให้บริการลูกค้าที่เป็นเลิศ",
  },
]);

const datajd = useDataJd();
const meta = useMeta();

const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["/"],
  },
});
const meta_tags = meta.metaTag({
  title: seoheader.data.title,
  description: seoheader.data.description,
  url: datajd.currentURL(),
  image: datajd.assets("/images/og/contact.jpg"),
});
useHead({
  title: seoheader.data.title,
  meta: [
    { name: "description", content: seoheader.data.description },
    ...(i18nHead.value.meta || []),
    ...meta_tags,
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});
useSchemaOrg([
  defineWebPage({
    name: seoheader.data.title,
    datePublished: moment("2023-05-03").toISOString(),
    dateModified: moment().toISOString(),
    description: seoheader.data.description,
    inLanguage: datajd.inLanguages(true),
    url: datajd.currentURL(),
    image: datajd.assets("/images/og/contact.jpg"),
    "@type": "ContactPage",
  }),
  defineBreadcrumb({
    itemListElement: [
      {
        item: {
          "@id": datajd.currentHost(),
          name: "Home",
        },
      },
      {
        item: {
          name: useI18n().t("contact"),
          "@id": datajd.currentHost() + "/contact",
        },
      },
    ],
  }),
]);
</script>
